<!--
  Modal: manage linked elements for a passport table field.
  Shows all elements with checkboxes; already-linked = pre-checked.
  Submit sends the complete desired value_list via PUT passport fields.
-->
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { keepPreviousData, useQuery } from '@tanstack/vue-query'
import { useHttp, useTranslate } from '@/shared/lib'
import { VIcons } from '@/shared/ui'
import { buildLinkPayload } from '../model/objectPassportLinkedLinkPayload'
import { usePendingFieldLinksOptional } from '../model/usePendingFieldLinks'
import { resolveFieldInventoryTypeId } from '../model/objectPassportFieldAdapter'
import type { ObjectPassportFieldDefinition, ObjectPassportFieldValue } from '../model/types'

const props = defineProps<{
  modelValue: boolean
  objectListId: number
  field: ObjectPassportFieldDefinition
  value?: ObjectPassportFieldValue | null
  inventoryGroupObjectId: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  attached: []
}>()

const { trs } = useTranslate()
const http = useHttp()
const pendingFieldLinks = usePendingFieldLinksOptional()

const filter = ref({ search: '', page: 1, per_page: 30 })
const searchInput = ref('')
const desiredIds = ref<Set<number>>(new Set())

const inventTypeId = computed(() => resolveFieldInventoryTypeId(props.field))

/**
 * Текущие привязанные ID из value (value_list для many, value для single).
 */
function currentLinkedIds(): number[] {
  const list = props.value?.value_list
  if (list?.length) return list.filter((id) => Number.isFinite(id) && id > 0)
  const single = Number(props.value?.value ?? 0)
  return Number.isFinite(single) && single > 0 ? [single] : []
}

const queryEnabled = computed(
  () => props.modelValue && props.inventoryGroupObjectId.length > 0 && inventTypeId.value > 0,
)

const elementsQuery = useQuery({
  queryKey: computed(() => [
    'object_passport_link_table_modal_elements',
    props.inventoryGroupObjectId,
    inventTypeId.value,
    filter.value.search,
    filter.value.page,
    filter.value.per_page,
  ]),
  queryFn: async () => {
    const searchParams = new URLSearchParams({
      search: filter.value.search,
      page: String(filter.value.page),
      per_page: String(filter.value.per_page),
    })
    const url = `/inventory/objects/${props.inventoryGroupObjectId}/inventory-types/${inventTypeId.value}/elements?${searchParams}`
    return http.get(url)
  },
  enabled: queryEnabled,
  placeholderData: keepPreviousData,
  retry: false,
})

type ElementRow = { object_list_id: number; name: string }

const rawRows = computed<ElementRow[]>(() => {
  const res = elementsQuery.data.value as { data?: unknown } | undefined
  if (!res) return []
  return Array.isArray(res.data) ? (res.data as ElementRow[]) : []
})

const total = computed(() => {
  const res = elementsQuery.data.value as { payload?: { total?: number } } | undefined
  return Number(res?.payload?.total ?? 0)
})

const isLoading = computed(() => elementsQuery.isFetching.value)

const headers = computed(() => [
  { title: '', key: 'checked', sortable: false, width: 48 },
  { title: trs('itf.id'), key: 'object_list_id', sortable: false },
  { title: trs('itf.name_label'), key: 'name', sortable: false },
])

function isChecked(id: number): boolean {
  return desiredIds.value.has(id)
}

function toggleId(id: number, checked: boolean): void {
  const next = new Set(desiredIds.value)
  if (checked) next.add(id)
  else next.delete(id)
  desiredIds.value = next
}

function close(): void {
  emit('update:modelValue', false)
}

function applySearch(): void {
  filter.value.search = searchInput.value
  filter.value.page = 1
}

function resetOnOpen(): void {
  filter.value = { search: '', page: 1, per_page: 30 }
  searchInput.value = ''
  const pending = pendingFieldLinks.patchFor(props.field.id)
  const baseIds = pending?.payload.value_list ?? currentLinkedIds()
  desiredIds.value = new Set(baseIds)
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) resetOnOpen()
  },
)

function onSave(): void {
  const ids = [...desiredIds.value]
  const current = currentLinkedIds()
  const currentSet = new Set(current)
  const addedIds = ids.filter((id) => !currentSet.has(id))
  const removedIds = current.filter((id) => !desiredIds.value.has(id))
  const addedRows = addedIds
    .map((id) => rawRows.value.find((r) => r.object_list_id === id))
    .filter((r): r is ElementRow => Boolean(r))
    .map((r) => ({ objectId: r.object_list_id, name: r.name }))
  pendingFieldLinks.stage({
    fieldId: props.field.id,
    payload: buildLinkPayload(props.field, ids),
    addedRows,
    removedIds,
  })
  emit('attached')
  emit('update:modelValue', false)
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    attach="body"
    :z-index="3400"
    max-width="1400"
    scrollable
    content-class="opltm-shell-dialog"
    :retain-focus="false"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <VCard class="opltm__card" rounded="xl">
      <VToolbar density="compact" color="surface" class="opltm__toolbar px-4 px-sm-6" flat>
        <VToolbarTitle class="text-h6 font-weight-bold">
          {{ trs('itf.passport_runtime_link_table_modal_title') }}
        </VToolbarTitle>
        <VSpacer />
        <VBtn icon variant="text" @click="close">
          <VIcons type="close" />
        </VBtn>
      </VToolbar>

      <VCardText class="opltm__body">
        <div class="opltm__body-inner d-flex flex-column gap-3">
          <VTextField
            v-model="searchInput"
            density="compact"
            variant="outlined"
            hide-details="auto"
            clearable
            :label="trs('itf.passport_runtime_link_table_search_label')"
            @keyup.enter="applySearch"
          />

          <VDataTableServer
            v-model:page="filter.page"
            v-model:items-per-page="filter.per_page"
            :loading="isLoading"
            :items="rawRows"
            :headers
            :items-length="total"
            :items-per-page-options="[10, 30, 50]"
            item-value="object_list_id"
          >
            <template #item.checked="{ item }">
              <VCheckbox
                :model-value="isChecked(item.object_list_id)"
                hide-details
                density="compact"
                @update:model-value="toggleId(item.object_list_id, Boolean($event))"
              />
            </template>
          </VDataTableServer>
        </div>
      </VCardText>

      <VCardActions class="opltm__actions px-4 px-sm-6 py-3">
        <VSpacer />
        <VBtn variant="text" @click="close">
          {{ trs('itf.passport_runtime_link_table_cancel') }}
        </VBtn>
        <VBtn
          color="primary"
          @click="onSave"
        >
          {{ trs('itf.passport_runtime_attach_selected') }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.opltm__card {
  display: flex;
  flex-direction: column;
  max-height: min(92vh, 980px);
  overflow: hidden;
  border-radius: 16px;
}

.opltm__toolbar {
  flex-shrink: 0;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
}

.opltm__body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 16px !important;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  overflow-x: hidden;
  overflow-y: auto;
}

@media (min-width: 600px) {
  .opltm__body {
    padding: 24px !important;
  }
}

.opltm__body-inner {
  flex: 1 1 auto;
  min-height: 0;
}

.opltm__actions {
  flex-shrink: 0;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  background: rgb(var(--v-theme-surface));
  border-radius: 0 0 16px 16px;
  overflow: visible;
}
</style>

<style lang="scss">
.opltm-shell-dialog {
  padding: 16px;
  box-sizing: border-box;
}
</style>
